import React, { useEffect, useState, useRef, useCallback } from 'react'
import Map, { Marker, Source, Layer } from 'react-map-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { useQuery, gql } from '@apollo/client'
import { riderById } from '../../apollo/queries'
import { subscriptionRiderLocation } from '../../apollo/subscriptions'
import ConfigurableValues from '../../config/constants'
import { getDirections } from '../../utils/mapbox'

const RIDER = gql`${riderById}`
const RIDER_LOC = gql`${subscriptionRiderLocation}`

const PRE_PICKUP = ['ASSIGNED', 'EN_ROUTE_PICKUP', 'ARRIVED_PICKUP']

const ROUTE_LAYER = {
  id: 'route-line',
  type: 'line',
  layout: { 'line-join': 'round', 'line-cap': 'round' },
  paint: { 'line-color': '#16A34A', 'line-width': 5, 'line-opacity': 0.85 }
}

const pinStyle = (bg) => ({
  width: 24,
  height: 24,
  borderRadius: '50%',
  background: bg,
  color: '#fff',
  border: '2px solid #fff',
  boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  fontWeight: 700
})

const LiveTrackingMap = ({ orderStatus, restCoordinates, destCoordinates, riderId }) => {
  const { MAPBOX_TOKEN } = ConfigurableValues()
  const { data, subscribeToMore } = useQuery(RIDER, {
    variables: { id: riderId },
    fetchPolicy: 'network-only',
    skip: !riderId
  })
  const [routeGeojson, setRouteGeojson] = useState(null)
  const mapRef = useRef(null)
  const lastLegRef = useRef(null)

  useEffect(() => {
    if (!riderId) return
    const unsub = subscribeToMore({
      document: RIDER_LOC,
      variables: { riderId },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev
        return { rider: { ...prev.rider, ...subscriptionData.data.subscriptionRiderLocation } }
      }
    })
    return unsub
  }, [riderId, subscribeToMore])

  const coords = data?.rider?.location?.coordinates
  const riderCoords = coords && coords.length === 2
    ? { lat: parseFloat(coords[1]), lng: parseFloat(coords[0]) }
    : null

  // Pick route leg: pre-pickup → rider→restaurant, post-pickup → rider→customer
  useEffect(() => {
    const isPrePickup = PRE_PICKUP.includes(orderStatus)
    const leg = isPrePickup ? 'pre' : 'post'
    const origin = riderCoords
    const dest = isPrePickup ? restCoordinates : destCoordinates
    if (!origin || !dest || origin.lat == null || dest.lat == null) return
    if (lastLegRef.current === leg && routeGeojson) return
    lastLegRef.current = leg
    let cancelled = false
    getDirections(origin, dest, MAPBOX_TOKEN)
      .then((res) => {
        if (!cancelled && res) setRouteGeojson(res.geojson)
      })
      .catch((e) => console.warn('[LiveTrackingMap] directions error', e?.message))
    return () => {
      cancelled = true
    }
  }, [orderStatus, riderCoords, restCoordinates, destCoordinates, routeGeojson, MAPBOX_TOKEN])

  // Fit bounds when we have points
  const fitBounds = useCallback(() => {
    const map = mapRef.current
    if (!map) return
    const pts = [restCoordinates, destCoordinates, riderCoords].filter(
      (p) => p && p.lat != null
    )
    if (pts.length === 0) return
    if (pts.length === 1) {
      map.easeTo({ center: [pts[0].lng, pts[0].lat], zoom: 14 })
      return
    }
    const lngs = pts.map((p) => p.lng)
    const lats = pts.map((p) => p.lat)
    map.fitBounds(
      [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)]
      ],
      { padding: 60, duration: 500 }
    )
  }, [restCoordinates, destCoordinates, riderCoords])

  useEffect(() => {
    fitBounds()
  }, [fitBounds])

  const center = restCoordinates || destCoordinates || riderCoords || { lat: 0, lng: 0 }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={{ longitude: center.lng, latitude: center.lat, zoom: 13 }}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/streets-v12"
        onLoad={fitBounds}
      >
        {restCoordinates && (
          <Marker longitude={restCoordinates.lng} latitude={restCoordinates.lat} anchor="center">
            <div style={pinStyle('#D32F2F')}>R</div>
          </Marker>
        )}
        {destCoordinates && (
          <Marker longitude={destCoordinates.lng} latitude={destCoordinates.lat} anchor="center">
            <div style={pinStyle('#1d4ed8')}>C</div>
          </Marker>
        )}
        {riderCoords && (
          <Marker longitude={riderCoords.lng} latitude={riderCoords.lat} anchor="center">
            <div style={{ ...pinStyle('#22c55e'), width: 18, height: 18 }} />
          </Marker>
        )}
        {routeGeojson && (
          <Source id="route" type="geojson" data={routeGeojson}>
            <Layer {...ROUTE_LAYER} />
          </Source>
        )}
      </Map>
    </div>
  )
}

export default LiveTrackingMap
